import { Dispatch, SetStateAction, useState } from "react";
import { MbscCalendarEvent } from "@mobiscroll/react";
import { Box, Button, Grid2, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { AccountCircle, Check, Close, Edit, Person, PinDrop } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { ScheduleEventBookingTypeContents } from "../../schedule-event-popover-booking-type-contents";
import { Colleague } from "../../../../features/Connections/types/Colleague.type";
import {
  BookingType,
  NotSerializedBookingMode
} from "../../../../features/Booking-Form/typings/booking-inputs";
import { MsTeamsLinkContents } from "../../schedule-event-popover-msTeams-link-Contents.component";
import { ScheduleEventExternalUser } from "../../schedule-event-external-user.component";
import {
  generateAddressDetail,
  ScheduleEventLocation
} from "../../schedule-event-location.component";
import { ScheduleEventTime } from "../../schedule-event-time.component";
import { ScheduleCheckInUnlockActions } from "../../schedule-checkin-unlock-actions.component";
import { ScheduleSelectionConferenceList } from "./ScheduleSelectionConferenceList.partial";
import { UserInformation } from "../../../../features/Login/typings/login.types";
import { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import { ScheduleContentList } from "./ScheduleContentList.component";
import { ShowMoreContentIcon } from "../../../FacilityManager/Components/Views/SelectFloorPlanView/SelectionPrompt/partial/ShowMoreContainerIcon.component";
import { useRemoteFetchBookingInventoryCharacteristics } from "../../../../hooks/Remote/Booking/useRemoteFetchBookingInventoryCharacteristics";
import { typeIdPerBookingMode } from "../../../BookingForm/functions/form.functions";
import DefaultSpinner from "../../../LoadingSpinner/default-spinner.component";
import { useFeatures } from "../../../FacilityManager/Hooks/useFeatures";
import { showBookingInventoryCharacteristics } from "./hooks/showBookingInventoryCharacteristics.hook.functions";
import { ShowMoreDescription } from "./ShowMoreDescription.component";
import { EnlargedImageCarouselDialog } from "../../../../features/EnlargedImageCarousel/enlarged-image-carousel-dialog.component";
import { EnlargedImagePreviewBox } from "../../../../features/EnlargedImageCarousel/partial/enlarged-image-preview-box.partial";
import { IZoneInventory } from "../../../FacilityManager/Domain/Types/FloorPlan/ZoneInventory.type";
import { IPlaceInventory } from "../../../FacilityManager/Domain/Types/FloorPlan/PlaceInventory.type";
import { getDisplayTripReason } from "../../../FleetManagement/LogbookDialog/functions/getDisplayTripReason.function";
import { updateVehicleBookingTimes } from "../../../../utils/axios-requests";
import { ChangeConferenceRoomDialog } from "../../ChangeConferenceRoom/change-conference-room-dialog.component";
import { canShowChangeConferenceRoomButton } from "../../ChangeConferenceRoom/change-conference-room.functions";
import { ScheduleResourceBookingBelowName } from "./partial/ScheduleResourceBookingBelowName.partial";
import { shouldShowResourceBookingBelowName } from "../../../../features/Schedule/utils/resource-booking-schedule.utils";

type P = {
  isMobileTablet: boolean;
  isMobile: boolean;
  isEditable: boolean;
  isColleagueScheduleEditable: boolean;
  /** Upcoming team-booking colleague only — not company-admin past/running edit. */
  isColleagueCheckInUnlockAllowed?: boolean;
  isConferenceServiceSetupLock: boolean;
  schedule: BookingScheduleInterface;
  schedulePictures: string[];
  pictureIsLoading: boolean;
  userInformation: UserInformation;
  bookingUser?: Colleague;
  /** When userBookedFor !== userBookedBy (e.g. book-on-behalf), the colleague who created the booking. */
  bookedByUser?: Colleague;
  allSchedules?: BookingScheduleInterface[];
  calendarData: MbscCalendarEvent[];
  isFirstAidSelected: boolean;
  handleEditSubject: (
    val: string,
    schedule: BookingScheduleInterface
  ) => Promise<string | number | void>;
  onScheduleChange?: (schedule: BookingScheduleInterface) => void;
  onClickUnlock?: () => void;
  refetchAllSchedule?: () => void;
  setCalendarData?: Dispatch<SetStateAction<MbscCalendarEvent[]>>;
  /** When false, hide booking user, booked-by-admin, and external participant details. */
  showResourceSensitiveBookingDetails?: boolean;
  /** When false (resource calendar), hide MS Teams link for conference bookings. */
  isColleagueMode?: boolean;
  /** Bubbled from ScheduleEventTime so the parent dialog can block backdrop close. */
  onTimeEditBlockingUiChange?: (isBlocking: boolean) => void;
};

export const ScheduleSelectionBookingTab: React.FC<P> = ({
  isMobileTablet,
  isMobile,
  isEditable,
  isColleagueScheduleEditable,
  isColleagueCheckInUnlockAllowed = false,
  isConferenceServiceSetupLock,
  schedule,
  schedulePictures,
  pictureIsLoading,
  userInformation,
  bookingUser,
  bookedByUser,
  allSchedules,
  calendarData,
  isFirstAidSelected,
  handleEditSubject,
  onScheduleChange,
  onClickUnlock,
  refetchAllSchedule,
  setCalendarData,
  showResourceSensitiveBookingDetails = true,
  isColleagueMode = true,
  onTimeEditBlockingUiChange
}) => {
  const { t } = useTranslation();
  const { useShowEquipmentInSlideOutPanel, useFindGoogleMapRoute } = useFeatures();

  const isVehicleBooking = schedule.bookingType === BookingType.VEHICLE;
  const isResourceBooking = schedule.bookingType === BookingType.RESOURCE;
  const isVisitOnlyBooking = schedule.isVisitOnly === true;
  const resourceBelowNameContent = shouldShowResourceBookingBelowName(schedule) ? (
    <ScheduleResourceBookingBelowName
      schedule={schedule}
      isEditable={isEditable}
      refetchAllSchedule={refetchAllSchedule}
    />
  ) : undefined;

  const { data: bookingInventoryCharacteristics, isFetching: isBICharacteristicsFetching } =
    useRemoteFetchBookingInventoryCharacteristics(
      typeIdPerBookingMode(schedule.bookingType as NotSerializedBookingMode)[0],
      {
        inventoryIds: [schedule.bookingInventoryId],
        start: schedule.start ?? schedule.startDate,
        end: schedule.end ?? schedule.endDate
      },
      !isVehicleBooking && !isVisitOnlyBooking
    );

  const [showMoreEquipment, setShowMoreEquipment] = useState(false);
  const [showMoreEquipCategory, setShowMoreEquipCategory] = useState(false);
  const [showMoreBookProp, setShowMoreBookProp] = useState(false);
  const [showMoreCategory, setShowMoreCategory] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [changeRoomDialogOpen, setChangeRoomDialogOpen] = useState(false);
  const [tripReasonEditValue, setTripReasonEditValue] = useState<string | null>(null);
  const [isTripReasonEditing, setIsTripReasonEditing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const contentChildItemStyle = (showMore: boolean) => {
    return { flexWrap: "nowrap", overflowY: "auto", maxHeight: showMore ? "180px" : "unset" };
  };

  const locDateTimeGridMbStyle = isMobileTablet ? 1 : 1.5;
  const browserRowWithPictureGap = schedulePictures.length > 0 ? 2 : 5;
  const rowGapStyle = isMobileTablet ? 2 : browserRowWithPictureGap;

  const { isShowCategory, isShowEquipCategories, isShowEquipments, isShowBookProps } =
    showBookingInventoryCharacteristics(
      bookingInventoryCharacteristics,
      isBICharacteristicsFetching,
      useShowEquipmentInSlideOutPanel
    );

  function handleGoogleLocationClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    event.stopPropagation();

    const inv = schedule.bookingInventory as IPlaceInventory | IZoneInventory;
    const { address, postalCode, city } = generateAddressDetail(
      inv?.locationInventory,
      inv?.floorInventory
    );
    const foundLoc = `${address} ${postalCode} ${city}`;

    /**
     * @description google map link, path finding: current to location
     * use if normal finding: `https://www.google.com/maps?q=${encodeURIComponent(address)}`
     */
    const url = `https://www.google.com/maps/dir/current+location/${encodeURIComponent(foundLoc)}`;
    window.open(url, "_blank");
  }

  const locationNotMobileWorking = schedule.locationName !== "Mobile Working";
  const hasPicturePaths = schedulePictures.length > 0;
  const showPlaceOrZoneButton =
    locationNotMobileWorking && !isVehicleBooking && !isResourceBooking && !isVisitOnlyBooking;
  const showChangeRoomButton = canShowChangeConferenceRoomButton({
    schedule,
    isEditable,
    isColleagueScheduleEditable
  });
  const activateBookingServices = userInformation.company?.meta?.activateBookingServices === true;
  const showExternalUser =
    showResourceSensitiveBookingDetails &&
    !isVisitOnlyBooking &&
    Boolean(schedule.externalName || schedule.externalEmail);
  const canEditTripReason =
    isVehicleBooking &&
    (isEditable || isColleagueScheduleEditable) &&
    refetchAllSchedule &&
    !schedule.checkedOut;

  const handleTripReasonSave = async () => {
    if (!canEditTripReason || tripReasonEditValue === null) return;
    const trimmed = tripReasonEditValue.trim();
    const current = (schedule.tripReason ?? "").trim();
    if (trimmed === current) {
      setIsTripReasonEditing(false);
      setTripReasonEditValue(null);
      return;
    }
    const startIso = schedule.start ?? schedule.startDate;
    const endIso = schedule.end ?? schedule.endDate;
    if (!startIso || !endIso) return;
    try {
      await updateVehicleBookingTimes({
        bookingId: schedule.id,
        newStartTime: startIso,
        newEndTime: endIso,
        bookingType: BookingType.VEHICLE,
        tripReason: trimmed === "" ? undefined : trimmed
      });
      refetchAllSchedule?.();
      enqueueSnackbar(t("Successfully edited booking"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("_scheduleSelection_tripReasonEditError"), { variant: "error" });
    }
    setIsTripReasonEditing(false);
    setTripReasonEditValue(null);
  };

  const handleTripReasonCancel = () => {
    setIsTripReasonEditing(false);
    setTripReasonEditValue(null);
  };

  return (
    <Grid2
      container
      data-testid="booking-selection-tabPanel-0-booking"
      rowGap={rowGapStyle}
      sx={[
        isMobileTablet ? { width: "fit-content" } : { width: "auto" },
        isMobileTablet ? { mt: 0 } : { mt: 2 }
      ]}
    >
      {hasPicturePaths && (
        <Grid2
          sx={{ width: "100%" }}
          data-testid="booking-selection-tabPanel-0-booking-show-booking-inventory"
        >
          <EnlargedImagePreviewBox
            imageUrl={schedulePictures[0]}
            loading={pictureIsLoading}
            bookingTypeContents={
              <ScheduleEventBookingTypeContents
                schedule={schedule}
                userId={userInformation.sub}
                bookingUser={bookingUser}
                showConferenceVisitorContactLink={isColleagueMode}
                belowNameContent={resourceBelowNameContent}
              />
            }
            buttonChild={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {showPlaceOrZoneButton && (
                  <Button
                    data-testid="schedule-item-show-btn"
                    onClick={() => onScheduleChange?.(schedule)}
                    sx={{ whiteSpace: "nowrap", maxHeight: "36.5px" }}
                  >
                    {schedule.bookingType === BookingType.CONFERENCEZONE
                      ? t("Show Zone")
                      : t("Show place")}
                  </Button>
                )}
                {showChangeRoomButton && (
                  <Button
                    data-testid="schedule-item-change-room-btn"
                    onClick={() => setChangeRoomDialogOpen(true)}
                    sx={{ whiteSpace: "nowrap", maxHeight: "36.5px" }}
                  >
                    {t("_changeRoom_action")}
                  </Button>
                )}
              </Box>
            }
            onViewClick={() => setImageDialogOpen(true)}
          />
        </Grid2>
      )}

      <Grid2
        container
        data-testid="booking-selection-tabPanel-0-booking-child-parent"
        sx={{ width: "100%" }}
      >
        <Grid2
          size={{ xs: 12, sm: showExternalUser ? 5 : 12 }}
          data-testid="booking-selection-tabPanel-0-booking-child1"
        >
          <Grid2 container flexDirection={"column"}>
            {/* display booking user when not first aid selected and different from logged in user */}
            {showResourceSensitiveBookingDetails &&
              !isFirstAidSelected &&
              bookingUser?.userId !== userInformation.sub && (
                <Grid2 container alignItems="center" gap={1} sx={{ mb: 1 }}>
                  <AccountCircle fontSize="small" />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: "1rem", letterSpacing: "0.3px" }}
                  >
                    {bookingUser?.firstName} {bookingUser?.surname}
                  </Typography>
                </Grid2>
              )}

            {/* display booked by user if vehicle booking and different from booking user */}
            {showResourceSensitiveBookingDetails && isVehicleBooking && (
              <Grid2 container flexDirection={"column"} alignItems={"flex-start"} gap={0.5}>
                {bookedByUser && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Person fontSize="small" color="action" />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 400, fontSize: "0.875rem", letterSpacing: "0.3px" }}
                    >
                      {t("_fleetEventDetail_bookedBy")}: {bookedByUser.firstName}{" "}
                      {bookedByUser.surname}
                      {` (${t("Admin")})`}
                    </Typography>
                  </Box>
                )}
              </Grid2>
            )}

            {/* display schedule booking type icon and type name */}
            {!hasPicturePaths && (
              <ScheduleEventBookingTypeContents
                schedule={schedule}
                userId={userInformation.sub}
                bookingUser={bookingUser}
                showConferenceVisitorContactLink={isColleagueMode}
                belowNameContent={resourceBelowNameContent}
              />
            )}

            <Grid2 container flexDirection={"column"} alignItems={"flex-start"} gap={1}>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                {/* display schedule show place/zone button */}
                {!hasPicturePaths && showPlaceOrZoneButton && (
                  <Button
                    data-testid="schedule-item-show-btn"
                    onClick={() => onScheduleChange?.(schedule)}
                    sx={{ whiteSpace: "nowrap", maxHeight: "36.5px" }}
                  >
                    {schedule.bookingType === BookingType.CONFERENCEZONE
                      ? t("Show Zone")
                      : t("Show place")}
                  </Button>
                )}
                {!hasPicturePaths && showChangeRoomButton && (
                  <Button
                    data-testid="schedule-item-change-room-btn"
                    onClick={() => setChangeRoomDialogOpen(true)}
                    sx={{ whiteSpace: "nowrap", maxHeight: "36.5px" }}
                  >
                    {t("_changeRoom_action")}
                  </Button>
                )}
              </Box>

              {/* display ms teams meeting link button (colleague calendar only) */}
              {isColleagueMode && schedule.bookingType === BookingType.CONFERENCEZONE && (
                <MsTeamsLinkContents isMobile={isMobileTablet} selectedSchedule={schedule} />
              )}
            </Grid2>
          </Grid2>
        </Grid2>

        {showExternalUser && (
          <Grid2
            size={{ xs: 12, sm: 4 }}
            sx={[isMobile ? { pt: 1.5 } : {}]}
            data-testid="booking-selection-tabPanel-0-booking-child2"
          >
            <ScheduleEventExternalUser schedule={schedule} isMobile={isMobileTablet} />
          </Grid2>
        )}
      </Grid2>

      <Grid2
        size={{ xs: 12 }}
        container
        data-testid="booking-selection-tabPanel-0-booking-contents"
        rowGap={rowGapStyle}
      >
        <Grid2 container display={isMobile ? "inline" : "flex"}>
          {/* location grid */}
          {schedule.locationName && (
            <Grid2 size={{ xs: 12 }} sx={{ mb: 3 }}>
              <Grid2 sx={{ display: "flex", alignItems: "center", mb: locDateTimeGridMbStyle }}>
                <Typography variant={"subtitle1"} sx={{ fontWeight: "bold" }}>
                  {t("Location")}
                </Typography>

                {/* google map link */}
                {locationNotMobileWorking && useFindGoogleMapRoute && (
                  <Tooltip title={<Typography>{t("_googleMapLocDesc")}</Typography>}>
                    <IconButton
                      sx={{ mx: 0.5 }}
                      data-testid="google-map-loc-btn"
                      onClick={handleGoogleLocationClick}
                    >
                      <PinDrop />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid2>

              <ScheduleEventLocation schedule={schedule} />
            </Grid2>
          )}

          {/* date and time grid */}
          <Grid2 size={{ xs: 12 }} sx={{ mb: 3 }}>
            <Typography
              variant={"subtitle1"}
              sx={{ fontWeight: "bold", mb: locDateTimeGridMbStyle }}
            >
              {t("Date and Time")}
            </Typography>

            <ScheduleEventTime
              schedule={schedule}
              calendarData={calendarData}
              isEditable={isEditable}
              isColleagueScheduleEditable={isColleagueScheduleEditable}
              isConferenceServiceSetupLock={isConferenceServiceSetupLock}
              setCalendarData={setCalendarData}
              refetchAllSchedule={refetchAllSchedule}
              onBlockingUiChange={onTimeEditBlockingUiChange}
            />
          </Grid2>

          {/* trip reason (vehicle booking) */}
          {isVehicleBooking && (
            <Grid2 size={{ xs: 12 }} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: locDateTimeGridMbStyle }}
              >
                {t("Trip Reason")}
              </Typography>
              {isTripReasonEditing ? (
                <Grid2 container alignItems="flex-start" gap={0.5} sx={{ flexWrap: "nowrap" }}>
                  <TextField
                    data-testid="schedule-selection-trip-reason-edit"
                    fullWidth
                    maxRows={3}
                    multiline
                    placeholder={t("_scheduleSelection_tripReasonPlaceholder")}
                    value={tripReasonEditValue ?? schedule.tripReason ?? ""}
                    onChange={e => setTripReasonEditValue(e.target.value)}
                    slotProps={{ input: { maxLength: 500 } }}
                  />
                  <IconButton
                    data-testid="schedule-selection-trip-reason-save"
                    onClick={handleTripReasonSave}
                    size="small"
                    aria-label={t("Save")}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton
                    data-testid="schedule-selection-trip-reason-cancel"
                    onClick={handleTripReasonCancel}
                    size="small"
                    aria-label={t("Cancel")}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Grid2>
              ) : (
                <Grid2 container alignItems="center" gap={0.5} sx={{ flexWrap: "nowrap" }}>
                  <Typography variant="body1" color="text.secondary">
                    {getDisplayTripReason(schedule.tripReason, t("_logbook_noReasonProvided"))}
                  </Typography>
                  {canEditTripReason && (
                    <IconButton
                      data-testid="schedule-selection-trip-reason-edit-btn"
                      onClick={() => {
                        setTripReasonEditValue(schedule.tripReason ?? "");
                        setIsTripReasonEditing(true);
                      }}
                      size="small"
                      sx={{ padding: 0 }}
                      aria-label={t("Edit")}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                </Grid2>
              )}
            </Grid2>
          )}
        </Grid2>

        {/* unlock and check-in: own booking only — not company-admin calendar edit (#1577) */}
        <ScheduleCheckInUnlockActions
          schedule={schedule}
          allSchedules={allSchedules}
          refetchAllSchedule={refetchAllSchedule}
          onClickUnlock={onClickUnlock}
          isEditable={isEditable && schedule.userBookedFor === userInformation.sub}
          isColleagueCheckInUnlockAllowed={isColleagueCheckInUnlockAllowed}
        />

        {isBICharacteristicsFetching && (
          <DefaultSpinner style={{ padding: 0 }} size={20} text={t("Loading...")} />
        )}

        {/* when conferencezon booking, display subject, description, and zone category */}
        {schedule.bookingType === BookingType.CONFERENCEZONE && (
          <ScheduleSelectionConferenceList
            userInformation={userInformation}
            schedule={schedule}
            isEditable={isEditable}
            isColleagueScheduleEditable={isColleagueScheduleEditable}
            bookingInventoryCharacteristics={bookingInventoryCharacteristics}
            handleEditSubject={handleEditSubject}
            showMoreContent={showMoreCategory}
            setShowMoreContent={setShowMoreCategory}
            contentChildrenStyle={contentChildItemStyle(showMoreCategory)}
            isShowCategory={!isVisitOnlyBooking && (isShowCategory ?? false)}
          />
        )}

        {/* place category */}
        {!isVisitOnlyBooking &&
          schedule.bookingType !== BookingType.CONFERENCEZONE &&
          isShowCategory && (
            <ScheduleContentList
              contentIcon={
                <ShowMoreContentIcon
                  type={"Category"}
                  showMoreContent={showMoreCategory}
                  setShowMoreContent={setShowMoreCategory}
                />
              }
              contentTitle={t("Category")}
              contents={[]}
              contentChildren={
                <Grid2
                  container
                  rowGap={1}
                  flexDirection={"column"}
                  sx={{ ...contentChildItemStyle(showMoreCategory) }}
                >
                  {bookingInventoryCharacteristics?.assignedCategories.map((cat, index) => {
                    const length = bookingInventoryCharacteristics?.assignedCategories.length;
                    return (
                      <div key={cat.id}>
                        <Grid2 pb={1}>{cat.name}</Grid2>
                        {showMoreCategory && (
                          <ShowMoreDescription
                            description={cat.description}
                            index={index}
                            length={length}
                            spaceStyleY={1}
                          />
                        )}
                      </div>
                    );
                  })}
                </Grid2>
              }
            />
          )}

        {/* equipment categories list */}
        {!isVisitOnlyBooking && isShowEquipCategories && (
          <ScheduleContentList
            contentIcon={
              <ShowMoreContentIcon
                type={"Equipment Categories"}
                showMoreContent={showMoreEquipCategory}
                setShowMoreContent={setShowMoreEquipCategory}
              />
            }
            contentTitle={t("Equipment")}
            contents={[]}
            contentChildren={
              <Grid2
                container
                rowGap={1}
                flexDirection={"column"}
                sx={{ ...contentChildItemStyle(showMoreEquipCategory) }}
              >
                {bookingInventoryCharacteristics?.totalEquipmentCategories.map(
                  (equipCate, index) => {
                    const length =
                      bookingInventoryCharacteristics.totalEquipmentCategories?.length ?? 0;

                    return (
                      <div key={equipCate.category.id}>
                        <Grid2 container pb={1}>
                          <Grid2 size={{ xs: 9 }}>{equipCate.category.name}</Grid2>
                          {showMoreEquipCategory && (
                            <Grid2 size={{ xs: 2 }} display={"flex"} sx={{ placeContent: "end" }}>
                              {t("Qty")}
                              <Typography sx={{ pl: 1 }}>{equipCate.quantity}</Typography>
                            </Grid2>
                          )}
                        </Grid2>
                        {showMoreEquipCategory && (
                          <ShowMoreDescription
                            description={equipCate.category.description}
                            index={index}
                            length={length}
                            spaceStyleY={1}
                          />
                        )}
                      </div>
                    );
                  }
                )}
              </Grid2>
            }
          />
        )}

        {/* equipments list */}
        {!isVisitOnlyBooking && isShowEquipments && (
          <ScheduleContentList
            contentIcon={
              <ShowMoreContentIcon
                type={"Equipment"}
                showMoreContent={showMoreEquipment}
                setShowMoreContent={setShowMoreEquipment}
              />
            }
            contentTitle={t("Equipment")}
            contents={[]}
            contentChildren={
              <Grid2
                container
                rowGap={1}
                flexDirection={"column"}
                sx={{ ...contentChildItemStyle(showMoreEquipment) }}
              >
                {bookingInventoryCharacteristics?.assignedEquipments.map((equip, index) => {
                  const length = bookingInventoryCharacteristics?.assignedEquipments.length;
                  return (
                    <div key={equip.id}>
                      <Grid2 pb={1}>{equip.equipment.name}</Grid2>
                      {showMoreEquipment && (
                        <ShowMoreDescription
                          description={equip.equipment.description}
                          index={index}
                          length={length}
                          spaceStyleY={1}
                        />
                      )}
                    </div>
                  );
                })}
              </Grid2>
            }
          />
        )}

        {/* booking properties list */}
        {!isVisitOnlyBooking && isShowBookProps && (
          <ScheduleContentList
            contentIcon={
              <ShowMoreContentIcon
                type={"Properties"}
                showMoreContent={showMoreBookProp}
                setShowMoreContent={setShowMoreBookProp}
              />
            }
            contentTitle={t("Properties")}
            contents={[]}
            contentChildren={
              <Grid2
                container
                rowGap={1}
                flexDirection={"column"}
                sx={{ ...contentChildItemStyle(showMoreBookProp) }}
              >
                {bookingInventoryCharacteristics?.assignedProperties.map((prop, index) => {
                  const length = bookingInventoryCharacteristics?.assignedProperties?.length ?? 0;
                  return (
                    <div key={prop.id}>
                      <Grid2 pb={1}>{prop.name}</Grid2>
                      {showMoreBookProp && (
                        <ShowMoreDescription
                          description={prop.description}
                          index={index}
                          length={length}
                          spaceStyleY={1}
                        />
                      )}
                    </div>
                  );
                })}
              </Grid2>
            }
          />
        )}

        {/* Vehicle booking: intentionally no fleet child content in this panel */}
      </Grid2>

      <EnlargedImageCarouselDialog
        handleClose={() => setImageDialogOpen(false)}
        isOpen={imageDialogOpen}
        picturePaths={schedulePictures}
        isLoading={pictureIsLoading}
      />

      {showChangeRoomButton && (
        <ChangeConferenceRoomDialog
          open={changeRoomDialogOpen}
          onClose={() => setChangeRoomDialogOpen(false)}
          schedule={schedule}
          activateBookingServices={activateBookingServices}
          refetchAllSchedule={refetchAllSchedule}
          allowPastSubjectEdit={isColleagueScheduleEditable}
        />
      )}
    </Grid2>
  );
};
